import { Dispatch } from 'redux';
import {
  isDeviceAndroid,
  isDeviceNotWeb,
} from '@nykaa/data-layer/utils/device';
import { webViewCallbacks } from '@nykaa/webview-wizard/constant';
import { webViewCallbackhandler } from '@nykaa/webview-wizard';
import { logger } from 'logging';
import { setAuthCartCount } from 'shared/store/auth/action';
import { addToCart, bulkAddToCart } from './service';
import { UNAUTHORIZE_ERROR_CODE } from 'shared/constants';
import couponActions from 'shared/store/coupons/actionTypes';
import { fetchProductOffers } from 'store/product/action';
import { getReactConfig } from 'store/auth/service';
import actions from 'store/auth/actionTypes';
import { ItemsObject } from '@nykaa/variant-selector/PLPVariantSelector/types';

export interface AddToCartParams {
  productId: string;
  isPro: boolean;
  trackingParam?: {
    site_navigation: string;
    site_sub_navigation: string;
    nykaa_store: string;
    interaction_location?: string;
  };
}

export interface BulkAddToCartParams {
  items: ItemsObject[];
  isPro: boolean;
  trackingParam?: {
    site_navigation: string;
    site_sub_navigation: string;
    nykaa_store: string;
  };
  buyNow?: boolean;
}

export const doAddToCart =
  (params: AddToCartParams) => (dispatch: Dispatch, getState: Function) => {
    const state = getState();
    const device = state.appReducer?.client.device;
    return addToCart(params, state)
      .then((cartData) => {
        const { itemsQuantity } = cartData;
        if (isDeviceNotWeb(device)) {
          const appParams = {
            device,
            callbackFunctionName: webViewCallbacks?.updateCartCount,
            callbackParams: { count: itemsQuantity },
          };
          webViewCallbackhandler(appParams);
        }
        dispatch(setAuthCartCount(itemsQuantity));

        return cartData;
      })
      .catch((error) => {
        const statusCode = error?.response?.status;
        const enableReactConfig =
          state?.remoteConfig?.switches?.enableReactConfigOnAddToCart;
        if (statusCode === UNAUTHORIZE_ERROR_CODE && enableReactConfig) {
          return getReactConfig().then((authData) => {
            dispatch({
              type: actions.FETCH_AUTH_DATA_SUCCESS,
              payload: authData,
            });
          });
        }
        logger.error(error, 'Error in Add to cart');
        throw error;
      });
  };

export const doAddToCartAndRefresh =
  (
    params: AddToCartParams,
    convenienceChargesEnabled?: boolean,
    buyNow?: boolean
  ) =>
  (dispatch: Dispatch, getState: Function) => {
    const state = getState();
    const device = state.appReducer?.client.device;
    return addToCart(params, state, true, convenienceChargesEnabled)
      .then((cartData) => {
        const { itemsQuantity, couponsApplied } = cartData;
        if (isDeviceNotWeb(device)) {
          const appParams = {
            device,
            callbackFunctionName: webViewCallbacks?.updateCartCount,
            callbackParams: isDeviceAndroid(device)
              ? itemsQuantity
              : { count: itemsQuantity },
          };
          webViewCallbackhandler(appParams);
        }
        if (!buyNow) {
          dispatch(setAuthCartCount(itemsQuantity));
        }
        dispatch({
          type: couponActions.REFRESH_COUPONS,
          payload: couponsApplied,
        });
        const isBestPriceEnabled =
          state?.remoteConfig?.switches?.isBestPriceEnabled;
        const isOfferApiV2Enabled =
          state?.remoteConfig?.switches?.isOfferApiV2Enabled;
        const offersParams = state.productPage?.offerRequestParams;
        const defaultDescForCoupons =
          state.remoteConfig.configs?.AB?.personalCoupons?.defaultDesc || '';
        if (offersParams) {
          dispatch(
            fetchProductOffers(
              offersParams,
              isOfferApiV2Enabled,
              defaultDescForCoupons,
              isBestPriceEnabled
            )
          );
        }
        return cartData;
      })
      .catch((error) => {
        const statusCode = error?.response?.status;
        const enableReactConfig =
          state?.remoteConfig?.switches?.enableReactConfigOnAddToCart;
        if (statusCode === UNAUTHORIZE_ERROR_CODE && enableReactConfig) {
          return getReactConfig().then((authData) => {
            dispatch({
              type: actions.FETCH_AUTH_DATA_SUCCESS,
              payload: authData,
            });
          });
        }
        logger.error(error, 'Error in Add to cart');
        throw error;
      });
  };

export const doBulkAddToCartAndRefresh =
  (params: BulkAddToCartParams, convenienceChargesEnabled?: boolean) =>
  (dispatch: Dispatch, getState: Function) => {
    const state = getState();
    const device = state.appReducer?.client.device;
    return bulkAddToCart(params, state, true, convenienceChargesEnabled)
      .then((cartData) => {
        const { itemsQuantity, couponsApplied } = cartData;
        if (isDeviceNotWeb(device)) {
          const appParams = {
            device,
            callbackFunctionName: webViewCallbacks?.updateCartCount,
            callbackParams: isDeviceAndroid(device)
              ? itemsQuantity
              : { count: itemsQuantity },
          };
          webViewCallbackhandler(appParams);
        }
        dispatch(setAuthCartCount(itemsQuantity));
        dispatch({
          type: couponActions.REFRESH_COUPONS,
          payload: couponsApplied,
        });
        const isBestPriceEnabled =
          state?.remoteConfig?.switches?.isBestPriceEnabled;
        const isOfferApiV2Enabled =
          state?.remoteConfig?.switches?.isOfferApiV2Enabled;
        const offersParams = state.productPage?.offerRequestParams;
        const defaultDescForCoupons =
          state.remoteConfig.configs?.AB?.personalCoupons?.defaultDesc || '';
        if (offersParams) {
          dispatch(
            fetchProductOffers(
              offersParams,
              isOfferApiV2Enabled,
              defaultDescForCoupons,
              isBestPriceEnabled
            )
          );
        }
        return cartData;
      })
      .catch((error) => {
        const statusCode = error?.response?.status;
        const enableReactConfig =
          state?.remoteConfig?.switches?.enableReactConfigOnAddToCart;
        if (statusCode === UNAUTHORIZE_ERROR_CODE && enableReactConfig) {
          return getReactConfig().then((authData) => {
            dispatch({
              type: actions.FETCH_AUTH_DATA_SUCCESS,
              payload: authData,
            });
          });
        }
        logger.error(error, 'Error in Add to cart');
        throw error;
      });
  };
